import React, { useId, useLayoutEffect, useRef, useState } from 'react'
import { Floating, Tooltip } from '../../../../../../shared/component/floating'
import { PLATFORMS, familiesFor } from './platforms'
import './platform-controls.scss'

// A course's platforms: small, quiet marks in one capsule, one segment per
// family it runs on (platforms.js FAMILIES): Web, Windows, then Android and
// Apple as their logo, a dot and their devices. Each mark names itself on
// hover. Nothing for a course that runs nowhere yet, or a folder -- unless
// `unpublished` is set: a grid card (course or template) then says "Not
// published" in the same capsule, so its footer is as tall as its
// neighbours' and the empty space reads as a state, not a gap (owner report
// 2026-10-13).
//
// On a `card` the capsule never clips (owner 2026-10-28): when it is wider
// than the card leaves it, families fold to their logo and a count ("+2"),
// Android first, then Apple. It is also a button that opens every build and
// what it covers, so a folded family is one tap away, and a touch screen,
// where tooltips never show, can still read it.
export const PlatformIcons = ({ platforms, unpublished, card }) => {
  const families = familiesFor(platforms)
  const [open, setOpen] = useState(false)
  const { ref, folded } = useFoldToFit(card ? families : [])

  if (!families.length) {
    if (!unpublished) return null
    return (
      <span className="node-platforms node-platforms-none" data-testid="node-platforms-none">
        <Tooltip label="No build of it is available on any platform yet" keepCase>
          <span className="node-platform node-platform-text">Not published</span>
        </Tooltip>
      </span>
    )
  }

  const marks = families.map(f => <Family key={f.key} family={f} folded={folded.includes(f.key)} tips={!open} />)
  if (!card) {
    return (
      <span className="node-platforms" role="group" aria-label="Runs on" data-testid="node-platforms">
        {marks}
      </span>
    )
  }

  const names = PLATFORMS.filter(p => platforms.includes(p.key)).map(p => p.label)
  return (
    <Floating placement="bottom-start" isOpen={open} onOpenChange={setOpen} content={<PlatformList platforms={platforms} />}>
      <span
        ref={ref}
        className="node-platforms"
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Runs on: ${names.join(', ')}`}
        data-testid="node-platforms">
        {marks}
      </span>
    </Floating>
  )
}

// One segment: a single icon, or a family's logo, a dot and its devices --
// folded, its logo and how many devices are behind it.
const Family = ({ family, folded, tips }) => {
  const logo = <Mark Icon={family.Icon} hover={family.hover} tips={tips} />
  if (!family.devices.length) return logo
  return (
    <span className={folded ? 'node-platform-family node-platform-folded' : 'node-platform-family'} data-family={family.key}>
      {logo}
      {folded
        ? <span className="node-platform-more" aria-hidden="true">+{family.devices.length}</span>
        : <>
            <span className="node-platform-dot" aria-hidden="true">·</span>
            {family.devices.map(d => <Mark key={d.hover} Icon={d.Icon} hover={d.hover} tips={tips} />)}
          </>}
    </span>
  )
}

const Mark = ({ Icon, hover, tips }) => (
  <Tooltip label={hover} keepCase disabled={!tips}>
    <span className="node-platform" role="img" aria-label={hover}>
      <Icon />
    </span>
  </Tooltip>
)

// The capsule's list: every build the course has, and what it runs on.
const PlatformList = ({ platforms }) => (
  <div className="node-platform-list" role="dialog" aria-label="Runs on">
    <div className="node-platform-list-title">Runs on</div>
    {PLATFORMS.filter(p => platforms.includes(p.key)).map(p =>
      <div key={p.key} className="node-platform-row">
        <span className="node-platform-row-icon" aria-hidden="true">
          <p.Icon />
        </span>
        <span>
          {p.label}
          <span className="node-platform-row-covers">{p.covers}</span>
        </span>
      </div>
    )}
  </div>
)

// Which families are folded so the capsule fits the width its card leaves
// it, in foldOrder: after each render, while the capsule is wider than it
// may be and a family is still unfolded, fold one more. A layout effect, so
// no frame shows it clipped. When that width changes (the window, the
// sidebar) or the course's platforms do, it starts again from none folded;
// each start is a new `fit`, so it measures again even when nothing was
// folded. The width watched is the capsule's parent: on a card, a block as
// wide as the name column, whatever the capsule's own width.
const useFoldToFit = families => {
  const ref = useRef(null)
  const order = families.filter(f => f.devices.length).sort((a, b) => a.foldOrder - b.foldOrder).map(f => f.key)
  const shape = families.map(f => `${f.key}${f.devices.length}`).join(' ')
  const [fit, setFit] = useState({ shape, folded: 0 })
  const folded = fit.shape === shape ? fit.folded : 0

  useLayoutEffect(() => {
    const el = ref.current
    if (el && folded < order.length && el.scrollWidth > el.clientWidth) setFit({ shape, folded: folded + 1 })
  }, [fit, shape, folded, order.length])

  useLayoutEffect(() => {
    const space = ref.current && ref.current.parentElement
    if (!space || typeof ResizeObserver === 'undefined') return
    let width = space.clientWidth
    const observer = new ResizeObserver(() => {
      if (space.clientWidth === width) return
      width = space.clientWidth
      setFit({ shape, folded: 0 })
    })
    observer.observe(space)
    return () => observer.disconnect()
  }, [shape])

  return { ref, folded: order.slice(0, folded) }
}

// The "Available on" filter beside the search box: any platform, or one.
export const PlatformSelect = ({ value, onChange }) => {
  const id = `platform-filter-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`

  return (
    <div className="input-group flex-nowrap platform-filter">
      <label className="input-group-text" htmlFor={id}>
        Available on
      </label>
      <select id={id} className="form-select" value={value || ''} onChange={e => onChange && onChange(e.target.value || null)}>
        <option value="">Any platform</option>
        {PLATFORMS.map(p =>
          <option key={p.key} value={p.key}>
            {p.label}
          </option>
        )}
      </select>
    </div>
  )
}
